import React, { useEffect, useRef, useState } from 'react';
import { formatToZMW, handlePrint, rootUrl, handleExcel } from '../../../helpers';
import axios from 'axios';
import { FaFolderOpen, FaPrint, FaSpinner, FaFileExcel } from 'react-icons/fa';
import { useOutletContext } from 'react-router-dom';

const CustomerData = ({ id, name }) => {
    const [data, setData] = useState([]); // To store the merged data (bills + receipts)
    const [balance, setBalance] = useState(0.0); // To track the balance
    const [opening, setOpening] = useState(0.0); // To track the balance
    const [loading, setLoading] = useState(false); // To manage loading state
    const [error, setError] = useState(null); // To handle errors


    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');


    useEffect(()=>{
        fetchData()
    }, [])

        const {allData, setAllData} = useOutletContext();

    const fetchData = () => {
        setLoading(true); 
        setError(null); 

    
        axios.get(rootUrl(`/customerStatement/${id}`))
            .then(response => {
                const { bills, receipts, suppliers } = response.data; 
                const mergedData = [...bills, ...receipts, ...suppliers];
              
                setOpening(response.data.opening[0]?.open)
                
                // Sort the merged data by date (assuming 'date' is in a valid format for Date parsing)
                const sortedData = mergedData.sort((a, b) => new Date(a.date) - new Date(b.date));
    
                setData(sortedData); // Set sorted data
                setBalance(0.0); // Reset balance
    
                setData(mergedData); 
                setBalance(0.0); 
    
                // Calculate the balance
                mergedData.forEach(res => {
                    if (res.invoice_no != null) {
                        setBalance(prevBalance => prevBalance + parseFloat(res.amount));
                    } else {
                        setBalance(prevBalance => prevBalance - parseFloat(res.amount));
                    }
                });
            })
            .catch(error => {
                setError('Error fetching data');
                console.error(error);
            })
            .finally(() => setLoading(false));
    };

    const componentRef = useRef(null)

    return (
        <div className="w-full">
            {loading && (
              <div className="flex justify-center items-center py-6">
                <FaSpinner size={30} className="animate-spin text-blue-950" />
              </div>
            )}
            {error && <p className="text-red-500 text-sm mb-2">{error}</p>} 
            
            {data.length === 0 && !loading ? (
                <div className="flex flex-col justify-center items-center py-10">
                    <FaFolderOpen className="text-gray-400 text-6xl mb-2" />
                    <p className="text-gray-600 text-sm">No data available</p>
                </div>
            ) : (
                <>
                  <div className="flex justify-end gap-2 mb-3">
                    <button
                      onClick={() => handlePrint(componentRef)}
                      className="inline-flex items-center bg-blue-950 text-white px-3 py-2 rounded-lg text-xs md:text-sm hover:bg-blue-900"
                    >
                      <FaPrint className="mr-1" />
                      Print
                    </button>
                    <button
                      onClick={() => handleExcel(componentRef, `CustomerStatement`)}
                      className="inline-flex items-center bg-blue-950 text-white px-3 py-2 rounded-lg text-xs md:text-sm hover:bg-blue-900"
                    >
                      <FaFileExcel className="mr-1" />
                      Excel
                    </button>
                  </div>

                  <div className="flex flex-wrap gap-4 items-end justify-center mb-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="border border-gray-300 px-2 py-1 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="border border-gray-300 px-2 py-1 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div ref={componentRef} className="border border-gray-200 p-4 md:p-6 m-2 md:m-4 rounded-xl bg-white">
                    <div className="flex justify-center mb-2">
                      <img className="w-[200px]" src={rootUrl(`/${allData?.council[0]?.path}`)} alt="Council Logo" />
                    </div>

                    <h2 className="text-center font-bold text-2xl text-gray-900">{allData?.council[0]?.name}</h2>
                    <h3 className="text-center font-semibold text-lg text-gray-800 mt-1">Customer Statement Of Account</h3>
                    <hr className="my-3" />

                    <p className="text-center text-lg font-bold text-gray-800 mb-3">{name}</p>
                    
                    <table className="min-w-full border border-gray-300 text-sm">
                      <thead>
                        <tr className="text-left bg-blue-950 text-white">
                          <th className="border-b px-4 py-2">Date</th>
                          <th className="border-b px-4 py-2">Invoice #</th>
                          <th className="border-b px-4 py-2">Description</th>
                          <th className="border-b px-4 py-2">Debit</th>
                          <th className="border-b px-4 py-2">Credit</th>
                          <th className="border-b px-4 py-2">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border-b px-4 py-2">2025-01-01</td>
                          <td className="border-b px-4 py-2">-</td>
                          <td className="border-b px-4 py-2">Opening Balance</td>
                          <td className="border-b px-4 py-2">{formatToZMW(opening)}</td>
                          <td className="border-b px-4 py-2">0</td>
                          <td className="border-b px-4 py-2">{formatToZMW(opening)}</td>
                        </tr>
                        {(() => {
                          let runningBalance = 0;
                          runningBalance += parseFloat(opening) || 0;
                          let totalDebit = 0;
                          totalDebit = parseFloat(opening) || 0;
                          let totalCredit = 0;

                          const filtered = data.filter((res) => {
                            if (!startDate && !endDate) return true;
                            const resDate = new Date(res.date.substring(0, 10));
                            const start = startDate ? new Date(startDate) : null;
                            const end = endDate ? new Date(endDate) : null;
                          
                            if (start && resDate < start) return false;
                            if (end && resDate > end) return false;
                            return true;
                          });
                          
                          const rows = filtered.map((res, index) => {
                            const debit = res.invoice_no != null || res.comment != null ? parseFloat(res.amount) : 0;
                            const credit = res.invoice_no == null && res.comment == null ? parseFloat(res.amount) : 0;

                            if (allData.budget.find(a => a.id === res.coa)?.coa == "321040" && res.type == 1) {
                              return null;
                            }

                            if (allData.budget.find(a => a.id === res.coa_id)?.coa == "157004" && res.type == 1) {
                              return null;
                            }

                            if (res.amount == 0) {
                              return null;
                            }

                            totalDebit += parseFloat(debit);
                            totalCredit += parseFloat(credit);
                            
                            runningBalance += parseFloat(debit) - parseFloat(credit);
                          
                            return (
                              <tr key={index}>
                                <td className="border-b px-4 py-2">{res.date.substring(0, 10)}</td>
                                <td className="border-b px-4 py-2">
                                  {res.invoice_no != null ? res?.invoice_no : res?.receipt_no}
                                </td>
                                <td className="border-b px-4 py-2">{res.description}</td>
                                <td className="border-b px-4 py-2">{formatToZMW(debit)}</td>
                                <td className="border-b px-4 py-2">{formatToZMW(credit)}</td>
                                <td className="border-b px-4 py-2">{formatToZMW(runningBalance)}</td>
                              </tr>
                            );
                          });

                          return (
                            <>
                              {rows}
                              <tr className="bg-gray-100 font-semibold">
                                <td className="border-t px-4 py-2 text-right" colSpan="3">Total</td>
                                <td className="border-t px-4 py-2">{formatToZMW(totalDebit)}</td>
                                <td className="border-t px-4 py-2">{formatToZMW(totalCredit)}</td>
                                <td className="border-t px-4 py-2">{formatToZMW(runningBalance)}</td>
                              </tr>
                            </>
                          );
                        })()}
                      </tbody>
                    </table>
                  </div>
                </>
            )}
        </div>
    );
};

export default CustomerData;
